import { Controller } from "@hotwired/stimulus"
import { VehicleBalloonManager } from "./vehicle_balloon_manager"


// Controller otimizado para monitoramento de mapas
export default class extends Controller {
  static values = {
    apiKey: String,
    vehicles: Array,
    lat: { type: Number, default: -14 },
    lng: { type: Number, default: -55 },
    fences: { type: Array, default: [] }
  }

  static targets = ['map', 'mapTypeDropdown', 'mapTypeMenu', 'fencesToggle', 'fencesBadge']
  static outlets = ["monitoring-realtime"]

  connect() {
    this.vehicleMarkers = new Map()
    this.fenceOverlays = new Map()
    this.fencesVisible = true
    this.balloonManager = new VehicleBalloonManager(this)
    this.mapInitialized = false
    this.isAdjustingZoom = false  // Controle para evitar ajustes simultâneos
    this.lastResizeTime = 0       // Para debounce de resize
    this.focusedVehicleId = null  // Controle de veículo focado
    this.zoomChangeSource = null  // Rastrear origem da mudança de zoom
    this.lastZoomValue = null     // Último valor de zoom conhecido
    
    // Guardar referência do controller no elemento para auto-refresh
    this.element.monitoringMapController = this
    
    // Aguardar carregamento do Google Maps
    this.boundHandleGoogleMapsLoaded = this.handleGoogleMapsLoaded.bind(this)
    this.boundHandleGoogleMapsError = this.handleGoogleMapsError.bind(this)
    this.boundHandleVehiclesUpdated = this.handleVehiclesUpdated.bind(this)
    this.boundHandleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)
    
    document.addEventListener('google-maps-loaded', this.boundHandleGoogleMapsLoaded)
    document.addEventListener('google-maps-error', this.boundHandleGoogleMapsError)
    document.addEventListener('vehicles:updated', this.boundHandleVehiclesUpdated)
    document.addEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)
    
    if (window.google && window.google.maps && window.google.maps.event) {
      this.boundHandleMapResize = this.handleMapResize.bind(this)
      window.addEventListener('resize', this.boundHandleMapResize)
    }
    
    this.checkGoogleMapsReady()
    
    setTimeout(() => {
      if (!this.mapInitialized) {
        this.checkGoogleMapsReady()
      } else {
        this.ensureProperZoom()
      }
    }, 300)
  }

  disconnect() {
    if (this.boundHandleGoogleMapsLoaded) {
      document.removeEventListener('google-maps-loaded', this.boundHandleGoogleMapsLoaded)
    }
    if (this.boundHandleGoogleMapsError) {
      document.removeEventListener('google-maps-error', this.boundHandleGoogleMapsError)
    }
    if (this.boundHandleVehiclesUpdated) {
      document.removeEventListener('vehicles:updated', this.boundHandleVehiclesUpdated)
    }
    if (this.boundHandleTurboFrameLoad) {
      document.removeEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)
    }
    if (this.boundHandleMapResize) {
      window.removeEventListener('resize', this.boundHandleMapResize)
    }

    if (this.balloonManager) {
      this.balloonManager.destroy()
      this.balloonManager = null
    }

    this.clearFences()

    if (this.fenceTooltip) {
      this.fenceTooltip.remove()
      this.fenceTooltip = null
    }

    if (this.vehicleMarkers) {
      this.vehicleMarkers.forEach(({ marker }) => {
        marker.setMap(null)
      })
      this.vehicleMarkers.clear()
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    
    if (this.resizeObserverTimeout) {
      clearTimeout(this.resizeObserverTimeout)
      this.resizeObserverTimeout = null
    }
    

    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map)
      this.map = null
    }

    if (this.boundHandleWindowResize) {
      window.removeEventListener('resize', this.boundHandleWindowResize)
    }
    if (this.boundHandleGoogleMapsResize && window.google && window.google.maps && window.google.maps.event) {
      google.maps.event.clearListeners(window, 'resize')
    }
    
    if (this.element.monitoringMapController === this) {
      delete this.element.monitoringMapController
    }
    
    this.mapInitialized = false
  }

  handleTurboFrameLoad(event) {
    if (event.target.contains(this.element)) {
      setTimeout(() => this.ensureProperZoom(), 100)
    }
  }

  ensureProperZoom() {
    if (this.mapInitialized && this.vehicleMarkers.size > 0) {
      this.waitForMapDimensionsAndFit()
    }
  }

  handleMapResize() {
    if (this.mapInitialized && this.map) {
      setTimeout(() => {
        if (this.vehicleMarkers.size > 0) {
          this.fitMapToMarkers()
        }
      }, 150)
    }
  }

  checkGoogleMapsReady() {
    const isMarkedAsLoaded = document.body.getAttribute('data-google-maps-loaded') === 'true'
    
    if (window.google && window.google.maps && window.google.maps.MapTypeId) {
      this.initMap()
    } else if (isMarkedAsLoaded && window.google) {
      setTimeout(() => this.checkGoogleMapsReady(), 200)
    }
  }

  handleGoogleMapsLoaded(event) {
    this.initMap()
  }

  handleGoogleMapsError(event) {
    this.showMapError('Falha ao carregar Google Maps')
  }

  initMap() {
    if (this.mapInitialized) return
    
    if (!this.hasMapTarget || !window.google?.maps) return

    try {
      this.createMap()
      this.renderVehicles()
      this.setupResizeListener()
      this.renderFences(this.fencesValue)
      this.mapInitialized = true
      
      this.waitForMapDimensionsAndFit()
    } catch (error) {
      this.showMapError(`Erro ao inicializar mapa: ${error.message}`)
    }
  }

  waitForMapDimensionsAndFit() {
    let attempts = 0
    const maxAttempts = 20
    
    const checkAndFit = () => {
      attempts++
      
      if (!this.mapTarget || attempts > maxAttempts) {
        setTimeout(() => this.fitMapToMarkers(), 500)
        return
      }
      
      const containerHeight = this.mapTarget.offsetHeight
      
      if (containerHeight >= 250) {
        setTimeout(() => this.fitMapToMarkers(), 200)
      } else {
        setTimeout(checkAndFit, 100)
      }
    }
    
    setTimeout(checkAndFit, 300)
  }

  createMap() {
    this.mapTarget.innerHTML = ''
    this.mapTarget.style.width = '100%'
    this.mapTarget.style.backgroundColor = '#e5e7eb'
    
    this.mapFullyLoaded = false
    
    this.map = new google.maps.Map(this.mapTarget, {
      zoom: 6,
      center: { lat: this.latValue, lng: this.lngValue },
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: false,
      rotateControl: false,
      scaleControl: false
    })

    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.mapFullyLoaded = true
      
      setTimeout(() => {
        this.fitMapToMarkers()
      }, 300)
    })

    this.map.addListener('click', (event) => {
      if (this.focusedVehicleId) {
        this.clearVehicleFocus()
      }
    })

    this.map.addListener('zoom_changed', () => {
      const currentZoom = this.map.getZoom()
      if (this.lastZoomValue !== null && this.lastZoomValue !== currentZoom) {
        if (!this.zoomChangeSource) {
          this.logZoomChange('USER_INTERACTION', 'EXTERNAL', this.lastZoomValue, currentZoom, 'Usuário alterou zoom')
        }
      }
      this.lastZoomValue = currentZoom
      this.zoomChangeSource = null
    })

    setTimeout(() => {
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize')
      }
    }, 500)
  }


  renderVehicles() {
    if (!Array.isArray(this.vehiclesValue)) return

    
    this.vehiclesValue.forEach((vehicle, index) => {
      
      const marker = this.createVehicleMarker(vehicle)
      if (!marker) {
      } else {
      }
    })
    this.fitMapToMarkers()
  }
  updateVehicleData(newVehicleData) {
    
    if (!this.mapInitialized || !Array.isArray(newVehicleData)) {
      return
    }

    // Atualizar dados internos
    this.vehiclesValue = newVehicleData
    
    // Atualizar markers existentes
    this.updateExistingMarkers(newVehicleData)
    
    // Fechar balões abertos se veículo não existe mais
    this.balloonManager.closeAllBalloons()
    
    // NÃO ajustar zoom durante refresh de dados - apenas atualizar markers
    // O zoom deve permanecer como está durante atualizações de dados
  }

  // Método otimizado para atualizar apenas os markers que mudaram
  updateExistingMarkers(newVehicleData) {
    const newVehicleMap = new Map()
    newVehicleData.forEach(vehicle => {
      newVehicleMap.set(vehicle.id, vehicle)
    })

    // Atualizar ou remover markers existentes
    for (const [vehicleId, markerData] of this.vehicleMarkers.entries()) {
      const newVehicleData = newVehicleMap.get(vehicleId)
      
      if (newVehicleData) {
        // Atualizar posição e ícone se necessário
        this.updateMarkerData(markerData, newVehicleData)
        newVehicleMap.delete(vehicleId) // Remover da lista de novos
      } else {
        // Veículo não existe mais, remover marker
        markerData.marker.setMap(null)
        this.vehicleMarkers.delete(vehicleId)
      }
    }

    // Adicionar novos markers
    for (const [vehicleId, vehicleData] of newVehicleMap.entries()) {
      const marker = this.createVehicleMarker(vehicleData)
      if (!marker) {
      }
    }
  }

  // Atualizar dados de um marker existente
  updateMarkerData(markerData, newVehicleData) {
    const { marker, vehicle: oldVehicleData } = markerData
    
    // Atualizar posição se mudou
    if (oldVehicleData.lat !== newVehicleData.lat || oldVehicleData.lng !== newVehicleData.lng) {
      marker.setPosition({
        lat: newVehicleData.lat,
        lng: newVehicleData.lng
      })
    }
    
    // Atualizar ícone se status mudou
    if (oldVehicleData.status !== newVehicleData.status) {
      marker.setIcon(this.getMarkerIcon(newVehicleData.status))
    }
    
    // Atualizar dados armazenados
    markerData.vehicle = newVehicleData
    
    // Re-bind click listener com novos dados
    google.maps.event.clearListeners(marker, 'click')
    marker.addListener('click', (event) => {
      if (event.domEvent) {
        event.domEvent.stopPropagation()
      }
      
      setTimeout(() => {
        this.balloonManager.closeAllBalloons()
        this.balloonManager.showBalloon(newVehicleData, marker)
      }, 10)
    })
  }

  // Handler para eventos de atualização
  handleVehiclesUpdated(event) {
    if (event.detail && event.detail.vehicles) {
      this.updateVehicleData(event.detail.vehicles)
    }
  }

  createVehicleMarker(vehicle) {    
    if (!this.isValidPosition(vehicle.lat, vehicle.lng)) {
      return null
    }
    const position = new google.maps.LatLng(vehicle.lat, vehicle.lng)
    
    const marker = new google.maps.Marker({
      position: position,
      map: this.map,
      title: `Veículo ${vehicle.plate}`,
      icon: this.getMarkerIcon(vehicle.status)
    })

    marker.addListener('click', (event) => {
      if (event.domEvent) {
        event.domEvent.stopPropagation()
      }
      
      setTimeout(() => {
        this.balloonManager.closeAllBalloons()
        this.balloonManager.showBalloon(vehicle, marker)
      }, 10)
    })

    this.vehicleMarkers.set(vehicle.id, { marker, vehicle })
    return marker
  }

  updateVehiclePositions(vehicles) {
    vehicles.forEach(vehicle => {
      const existing = this.vehicleMarkers.get(vehicle.id)
      
      if (existing) {
        existing.marker.setPosition({ lat: vehicle.lat, lng: vehicle.lng })
        existing.marker.setIcon(this.getMarkerIcon(vehicle.status))
        existing.vehicle = vehicle
      } else {
        const newMarker = this.createVehicleMarker(vehicle)
        if (!newMarker) {
        }
      }
    })
  }

  getMarkerIcon(status) {
    const color = status === 'online' ? '#10B981' : '#EF4444'
    
    const svgMarker = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="${color}" stroke="white" stroke-width="2"/>
        <circle cx="12" cy="12" r="4" fill="white"/>
      </svg>
    `
    
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarker),
      size: new google.maps.Size(24, 24),
      anchor: new google.maps.Point(12, 12)
    }
  }

  fitMapToMarkers() {
    this.logZoomChange('FIT_MAP_TO_MARKERS', 'START', this.map?.getZoom() || 0, 'N/A', `Iniciando fit com ${this.vehicleMarkers.size} veículos`)
    
    if (!this.map || this.vehicleMarkers.size === 0) {
      return
    }

    // Se há veículo focado, manter o foco nele em vez de ajustar para todos os veículos
    if (this.focusedVehicleId) {
      return
    }

    // Verificar se o container do mapa tem altura adequada
    const mapContainer = this.mapTarget
    if (!mapContainer) {
      return
    }
    
    const containerHeight = mapContainer.offsetHeight
    
    if (containerHeight < 200) {
      setTimeout(() => this.fitMapToMarkers(), 300)
      return
    }

    // Se há apenas um veículo, usar zoom fixo
    if (this.vehicleMarkers.size === 1) {
      const singleMarker = Array.from(this.vehicleMarkers.values())[0].marker
      const vehicle = Array.from(this.vehicleMarkers.values())[0].vehicle
      this.map.setCenter(singleMarker.getPosition())
      this.setZoomWithLogging(9, 'FIT_SINGLE_VEHICLE', `Único veículo: ${vehicle.plate}`)
      return
    }

    // Para múltiplos veículos, aplicar fitBounds
    const bounds = new google.maps.LatLngBounds()
    this.vehicleMarkers.forEach(({ marker, vehicle }) => {
      const position = marker.getPosition()
      bounds.extend(position)
    })

    
    const containerWidth = mapContainer.offsetWidth
    
    // Padding mais generoso para garantir que todos os veículos sejam visíveis
    // Especialmente importante no carregamento inicial
    const paddingTop = Math.max(120, containerHeight * 0.25)
    const paddingHorizontal = Math.max(120, containerWidth * 0.2)
    
    
    // Aplicar fitBounds com padding mais generoso
    this.map.fitBounds(bounds, { 
      top: paddingTop,
      right: paddingHorizontal, 
      bottom: paddingTop, 
      left: paddingHorizontal 
    })
    
    this.logZoomChange('FIT_BOUNDS', 'APPLIED', 'BEFORE', 'AFTER', `Padding: ${paddingTop}px / ${paddingHorizontal}px`)
    
    // Aguardar aplicação e validar zoom
    setTimeout(() => {
      this.validateZoomState('FIT_BOUNDS_VALIDATION')
      
      // Validação adicional para carregamento inicial
      // Se ainda estiver com zoom muito alto, forçar um zoom mais baixo
      setTimeout(() => {
        if (this.map && !this.focusedVehicleId) {
          const currentZoom = this.map.getZoom()
          const idealMaxZoom = this.getMaxZoomForVehicleCount()
          
          if (currentZoom > idealMaxZoom) {
            this.setZoomWithLogging(idealMaxZoom, 'FINAL_ADJUSTMENT', 'Correção final para visualização adequada')
          }
        }
      }, 200)
    }, 400)
  }

  // Método para garantir que todos os marcadores estão completamente visíveis (versão menos agressiva)
  ensureAllMarkersVisible() {
    if (!this.map || this.vehicleMarkers.size === 0) {
      return
    }
    
    if (this.isAdjustingZoom) {
      return
    }

    const mapBounds = this.map.getBounds()
    if (!mapBounds) {
      return
    }

    let markersOutside = []
    const currentZoom = this.map.getZoom()

    // Verificar apenas se há marcadores completamente fora dos bounds (não próximos à borda)
    this.vehicleMarkers.forEach(({ marker, vehicle }) => {
      const position = marker.getPosition()
      const isInside = mapBounds.contains(position)
      
      if (!isInside) {
        markersOutside.push(vehicle.plate || vehicle.id)
      }
    })

    // Só ajustar se há marcadores realmente fora dos bounds
    if (markersOutside.length > 0) {
      // Fazer apenas um ajuste pequeno
      if (currentZoom > 3) {
        const newZoom = Math.max(3, currentZoom - 1)
        this.map.setZoom(newZoom)
      }
    }
  }

  // Verificar se uma posição está seguramente visível (longe das bordas)
  isPositionSafelyVisible(position, mapBounds) {
    const lat = position.lat()
    const lng = position.lng()
    
    const north = mapBounds.getNorthEast().lat()
    const south = mapBounds.getSouthWest().lat()
    const east = mapBounds.getNorthEast().lng()
    const west = mapBounds.getSouthWest().lng()
    
    // Calcular margem de segurança (15% das dimensões do mapa)
    const latMargin = (north - south) * 0.15
    const lngMargin = (east - west) * 0.15
    
    // Verificar se está dentro dos bounds com margem
    return (lat >= south + latMargin && 
            lat <= north - latMargin && 
            lng >= west + lngMargin && 
            lng <= east - lngMargin)
  }

  // Definir zoom máximo baseado na quantidade de veículos
  getMaxZoomForVehicleCount() {
    const count = this.vehicleMarkers.size
    if (count === 1) return 9
    if (count <= 3) return 8  // Reduzido para permitir zoom mais baixo
    if (count <= 5) return 7  // Reduzido para permitir zoom mais baixo
    if (count <= 10) return 6  // Reduzido para permitir zoom mais baixo
    if (count <= 20) return 5  // Reduzido para permitir zoom mais baixo
    return 4  // Para muitos veículos - mais baixo ainda
  }

  setupResizeListener() {
    if (window.ResizeObserver && this.hasMapTarget) {
      
      // Debounce agressivo para ResizeObserver
      this.resizeObserverTimeout = null
      
      const resizeObserver = new ResizeObserver(() => {
        // Cancelar timeout anterior se existir
        if (this.resizeObserverTimeout) {
          clearTimeout(this.resizeObserverTimeout)
        }
        
        // Debounce de 500ms para evitar loop infinito
        this.resizeObserverTimeout = setTimeout(() => {
          if (this.map && !this.isAdjustingZoom) {
            google.maps.event.trigger(this.map, 'resize')
            this.fitMapToMarkers()
          }
          this.resizeObserverTimeout = null
        }, 500)
      })
      
      resizeObserver.observe(this.mapTarget)
      this.resizeObserver = resizeObserver
    }

    // Listener para resize da janela
    this.boundHandleWindowResize = this.handleWindowResize.bind(this)
    window.addEventListener('resize', this.boundHandleWindowResize)
    
    // Listener específico para eventos de resize do Google Maps (disparado pelo map_resizer_controller)
    this.boundHandleGoogleMapsResize = this.handleGoogleMapsResize.bind(this)
    if (window.google && window.google.maps && window.google.maps.event) {
      google.maps.event.addListener(window, 'resize', this.boundHandleGoogleMapsResize)
    }
  }

  handleWindowResize() {
    if (this.map) {
      google.maps.event.trigger(this.map, 'resize')
    }
  }

  // Handler específico para resize do Google Maps (expandir/recolher)
  handleGoogleMapsResize() {
    if (!this.mapInitialized || !this.map) return

    // Debounce: ignorar se já está ajustando zoom ou se foi muito recente
    const now = Date.now()
    if (this.isAdjustingZoom || (now - this.lastResizeTime) < 500) {
      return
    }

    this.lastResizeTime = now
    this.isAdjustingZoom = true
    
    // Aguardar o mapa se estabilizar após mudança de tamanho
    setTimeout(() => {
      if (this.vehicleMarkers.size > 0) {
        this.forceRecalculateBounds()
      }
      // Liberar o lock após processamento (tempo maior para evitar conflitos)
      setTimeout(() => {
        this.isAdjustingZoom = false
      }, 2000)
    }, 300)
  }

  // Método para forçar recálculo completo dos bounds após resize
  forceRecalculateBounds() {
    if (!this.map || this.vehicleMarkers.size === 0) {
      return
    }
    
    // Se há veículo focado, manter o foco nele durante resize
    if (this.focusedVehicleId) {
      // Trigger resize no mapa para garantir dimensões corretas
      google.maps.event.trigger(this.map, 'resize')
      return
    }
    
    // Trigger resize no mapa para garantir dimensões corretas
    google.maps.event.trigger(this.map, 'resize')
    
    // Aguardar o resize ser processado
    setTimeout(() => {
      const mapContainer = this.mapTarget
      if (!mapContainer) {
        return
      }
      
      const containerHeight = mapContainer.offsetHeight
      const containerWidth = mapContainer.offsetWidth
      
      // Recalcular bounds com padding apropriado para as novas dimensões
      const bounds = new google.maps.LatLngBounds()
      this.vehicleMarkers.forEach(({ marker }) => {
        bounds.extend(marker.getPosition())
      })

      // Se há apenas um veículo
      if (this.vehicleMarkers.size === 1) {
        const singleMarker = Array.from(this.vehicleMarkers.values())[0].marker
        this.map.setCenter(singleMarker.getPosition())
        this.setZoomWithLogging(9, 'FORCE_SINGLE_VEHICLE', `Único veículo durante resize`)
        return
      }

      // Para múltiplos veículos, calcular padding baseado nas novas dimensões
      const paddingTop = Math.max(80, containerHeight * 0.25)
      const paddingHorizontal = Math.max(80, containerWidth * 0.15)
      
      // Aplicar fitBounds com novo padding
      this.map.fitBounds(bounds, { 
        top: paddingTop,
        right: paddingHorizontal, 
        bottom: paddingTop, 
        left: paddingHorizontal 
      })
      
      // Verificar e ajustar zoom após bounds serem aplicados
      setTimeout(() => {
        this.enforceZoomLimitsAfterResize()
      }, 200)
      
    }, 100)
  }

  // Método para aplicar limites de zoom após resize
  enforceZoomLimitsAfterResize() {
    const currentZoom = this.map.getZoom()
    const minZoom = 5  // Zoom mínimo para evitar visão global
    const maxZoom = this.getMaxZoomForVehicleCount()
    
    
    // Aplicar limite mínimo se estiver muito baixo
    if (currentZoom < minZoom) {
      this.setZoomWithLogging(minZoom, 'ENFORCE_MIN_RESIZE', `Enforcement mínimo durante resize`)
    }
    // Aplicar limite máximo se estiver muito alto
    else if (currentZoom > maxZoom) {
      this.setZoomWithLogging(maxZoom, 'ENFORCE_MAX_RESIZE', `Enforcement máximo durante resize`)
    }
  }

  // Sistema centralizado de logging para zoom
  logZoomChange(source, action, oldZoom, newZoom, extraInfo = '') {
    this.lastZoomValue = newZoom
    this.zoomChangeSource = source
  }

  // Wrapper para setZoom com logging
  setZoomWithLogging(zoom, source, extraInfo = '') {
    if (!this.map) return
    
    const oldZoom = this.map.getZoom()
    this.logZoomChange(source, 'SET', oldZoom, zoom, extraInfo)
    this.zoomChangeSource = source  // Marcar que nós iniciamos esta mudança
    this.map.setZoom(zoom)
  }

  // Método para validar estado do zoom
  validateZoomState(source = 'VALIDATION') {
    if (!this.map || this.vehicleMarkers.size === 0) return
    
    const currentZoom = this.map.getZoom()
    
    // Zoom mínimo mais baixo para carregamento inicial
    const minZoom = 3  // Permitir zoom mais baixo para abranger todos os veículos
    const maxZoom = this.getMaxZoomForVehicleCount()
    
    // Se há veículo focado, não deve interferir no zoom
    if (this.focusedVehicleId) {
      return
    }
    
    
    // Verificar se zoom está fora dos limites
    if (currentZoom < minZoom) {
      this.setZoomWithLogging(minZoom, source, 'Correção de zoom muito baixo')
    } else if (currentZoom > maxZoom) {
      this.setZoomWithLogging(maxZoom, source, 'Correção de zoom muito alto')
    } else {
    }
  }

  // Função para validar se as coordenadas são válidas
  isValidPosition(lat, lng) {
    // Verificar se os valores existem e são números
    if (lat === null || lat === undefined || lng === null || lng === undefined) {
      return false
    }
    
    const numLat = parseFloat(lat)
    const numLng = parseFloat(lng)
    
    // Verificar se são números válidos
    if (isNaN(numLat) || isNaN(numLng)) {
      return false
    }
    
    // Verificar se não são coordenadas zeradas (meio do mar)
    if (numLat === 0 && numLng === 0) {
      return false
    }
    
    // Verificar se estão dentro dos limites geográficos válidos
    // Latitude: -90 a 90, Longitude: -180 a 180
    if (numLat < -90 || numLat > 90 || numLng < -180 || numLng > 180) {
      return false
    }
    
    return true
  }

  centerMapOnVehicle(vehicleId) {
    if (!this.map) {
      return
    }
    const vehicleData = this.vehicleMarkers.get(vehicleId)
    
    if (!vehicleData?.marker) {
      return
    }
    
    // Usar diretamente os dados do veículo em vez do marker
    const vehicle = vehicleData.vehicle
    if (!vehicle || !this.isValidPosition(vehicle.lat, vehicle.lng)) {
      return
    }
    
    
    // DEFINIR FOCO NO VEÍCULO
    this.focusedVehicleId = vehicleId
    
    // Aguardar o mapa estar totalmente carregado antes de centralizar
    if (!this.mapFullyLoaded) {
      setTimeout(() => {
        this.centerMapOnVehicle(vehicleId)
      }, 500)
      return
    }
    
    // Zoom moderado para visualização detalhada
    const targetZoom = 16
    
    try {
      // Criar objeto LatLng explicitamente
      const lat = parseFloat(vehicle.lat)
      const lng = parseFloat(vehicle.lng)
      
      
      // Usar setCenter diretamente com objeto simples
      this.map.setCenter({ lat: lat, lng: lng })
      
      // Aplicar zoom com delay pequeno
      setTimeout(() => {
        if (this.map) {
          this.setZoomWithLogging(targetZoom, 'CENTER_VEHICLE', `Focando em ${vehicle.plate}`)
        }
      }, 300)
        
      // Destacar o marcador selecionado
      this.highlightVehicleMarker(vehicleData.marker)
      
    } catch (error) {
    }
  }

  highlightVehicleMarker(marker) {
    // Animação simples de bounce para destacar o marcador
    if (marker.setAnimation) {
      marker.setAnimation(google.maps.Animation.BOUNCE)
      
      // Parar animação após 2 segundos
      setTimeout(() => {
        marker.setAnimation(null)
      }, 2000)
    }
  }

  // Método para limpar o foco e mostrar todos os veículos
  clearVehicleFocus() {
    const previousFocusedId = this.focusedVehicleId
    
    // Limpar o foco primeiro
    this.focusedVehicleId = null
    
    // Aguardar um tick para garantir que a mudança seja processada
    setTimeout(() => {
      // Ajustar mapa para mostrar todos os veículos
      if (this.vehicleMarkers.size > 0) {
        this.fitMapToMarkers()
      } else {
      }
    }, 50)
  }

  updateVehicleMarker(vehicleData) {
    const existingVehicle = this.vehicleMarkers.get(vehicleData.id)
    
    if (existingVehicle && existingVehicle.marker) {
      // Atualizar posição do marcador existente
      const newPosition = new google.maps.LatLng(vehicleData.lat, vehicleData.lng)
      
      // Animação suave da movimentação
      const marker = existingVehicle.marker
      marker.setPosition(newPosition)
      
      // Atualizar dados do veículo para o popup
      existingVehicle.data = vehicleData
      
      // Destacar visualmente que foi atualizado
      this.highlightVehicleMarker(marker)
      
    } else {
      // Criar novo marcador se não existir
      this.addVehicleMarker(vehicleData)
    }
  }

  addVehicleMarker(vehicle) {
    if (!this.map || !vehicle.lat || !vehicle.lng) return
    
    const position = new google.maps.LatLng(vehicle.lat, vehicle.lng)
    const icon = this.createVehicleIcon(vehicle)
    
    const marker = new google.maps.Marker({
      position: position,
      map: this.map,
      icon: icon,
      title: `${vehicle.plate} - ${vehicle.driver}`,
      zIndex: this.getMarkerZIndex(vehicle)
    })
    
    // Evento de clique no marcador
    marker.addListener('click', () => {
      this.balloonManager.showBalloon(vehicle, marker)
    })
    
    // Armazenar referência
    this.vehicleMarkers.set(vehicle.id, {
      marker: marker,
      data: vehicle
    })
  }

  showMapError(message) {
    if (this.hasMapTarget) {
      this.mapTarget.innerHTML = `
        <div class="flex items-center justify-center h-full bg-red-50 border-2 border-red-200 rounded-lg">
          <div class="text-center p-8">
            <div class="text-6xl mb-4">❌</div>
            <h3 class="text-xl font-bold text-red-700 mb-2">Erro ao carregar Google Maps</h3>
            <p class="text-red-600">${message}</p>
            <button onclick="location.reload()" class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Tentar Novamente
            </button>
          </div>
        </div>
      `
    }
  }

  // Controles do tipo de mapa
  toggleMapTypeDropdown(event) {
    event.stopPropagation()
    
    if (this.hasMapTypeMenuTarget) {
      this.mapTypeMenuTarget.classList.toggle('hidden')
    }
    
    document.addEventListener('click', () => this.closeDropdown(), { once: true })
  }

  selectMapType(event) {
    const mapType = event.target.dataset.mapType
    
    if (this.map) {
      this.map.setMapTypeId(google.maps.MapTypeId[mapType.toUpperCase()])
    }
    
    if (this.hasMapTypeMenuTarget) {
      this.mapTypeMenuTarget.classList.add('hidden')
    }
  }

  closeDropdown() {
    if (this.hasMapTypeMenuTarget) {
      this.mapTypeMenuTarget.classList.add('hidden')
    }
  }

  // Método para mostrar todos os veículos (limpar foco e ajustar zoom)
  showAllVehicles() {
    
    // Limpar o foco do veículo específico
    this.clearVehicleFocus()
    
    // Feedback visual do botão
    const button = event.target.closest('button')
    if (button) {
      button.classList.add('scale-95')
      setTimeout(() => {
        button.classList.remove('scale-95')
      }, 150)
    }
  }

  // ─── Action handlers for view ──────────────────────────────────────────────

  selectVehicle(event) {
    const vehicleId = parseInt(event.currentTarget.dataset.vehicleId)
    if (vehicleId) this.centerMapOnVehicle(vehicleId)
  }

  showTab(event) {
    const tab = event.currentTarget.dataset.tab
    // Tab switching: purely visual for now
    const tabContainer = event.currentTarget.closest('.flex')
    if (tabContainer) {
      tabContainer.querySelectorAll('button').forEach(btn => {
        btn.classList.remove('text-blue-600', 'border-blue-600', 'border-b-2')
        btn.classList.add('text-gray-600', 'border-transparent', 'border-b-2')
      })
      event.currentTarget.classList.remove('text-gray-600', 'border-transparent')
      event.currentTarget.classList.add('text-blue-600', 'border-blue-600')
    }
  }

  // ─── Electronic Fences ─────────────────────────────────────────────────────

  renderFences(fences) {
    if (!Array.isArray(fences) || !this.map) return

    fences.forEach(fence => {
      const paths = this.buildFencePaths(fence.geometry)
      if (!paths) return

      const color = fence.color || '#00A0A0'
      const overlay = new google.maps.Polygon({
        paths,
        map: this.map,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.15,
        clickable: true
      })

      overlay.addListener('mouseover', (e) => this.showFenceTooltip(fence, e))
      overlay.addListener('mousemove', (e) => this.moveFenceTooltip(e))
      overlay.addListener('mouseout', () => this.hideFenceTooltip())

      this.fenceOverlays.set(fence.id, { overlay, fence })
    })

    if (this.hasFencesBadgeTarget) {
      this.fencesBadgeTarget.textContent = fences.length
    }
  }

  buildFencePaths(geometry) {
    if (!geometry) return null

    if (geometry.type === 'circle') {
      const { lat: clat, lng: clng } = geometry.center
      const radiusDeg = geometry.radius / 111320
      const points = []
      for (let i = 0; i < 64; i++) {
        const angle = (i * 2 * Math.PI) / 64
        points.push({
          lat: clat + radiusDeg * Math.sin(angle),
          lng: clng + radiusDeg * Math.cos(angle)
        })
      }
      return points
    }

    if (geometry.type === 'polygon' && Array.isArray(geometry.coordinates)) {
      return geometry.coordinates.map(c => ({ lat: c.lat, lng: c.lng }))
    }

    return null
  }

  toggleFences() {
    this.fencesVisible = !this.fencesVisible

    this.fenceOverlays.forEach(({ overlay }) => {
      overlay.setMap(this.fencesVisible ? this.map : null)
    })

    if (this.hasFencesToggleTarget) {
      const btn = this.fencesToggleTarget
      if (this.fencesVisible) {
        btn.classList.remove('opacity-50')
        btn.title = 'Ocultar cercas eletrônicas'
      } else {
        btn.classList.add('opacity-50')
        btn.title = 'Mostrar cercas eletrônicas'
      }
    }
  }

  clearFences() {
    if (this.fenceOverlays) {
      this.fenceOverlays.forEach(({ overlay }) => overlay.setMap(null))
      this.fenceOverlays.clear()
    }
  }

  // ─── Fence tooltip ─────────────────────────────────────────────────────────

  getFenceTooltip() {
    if (!this.fenceTooltip) {
      const el = document.createElement('div')
      el.style.cssText = [
        'position:fixed',
        'z-index:9999',
        'pointer-events:none',
        'background:rgba(255,255,255,0.95)',
        'border:1px solid rgba(0,0,0,0.12)',
        'border-radius:6px',
        'padding:5px 10px',
        'font-size:12px',
        'font-weight:600',
        'color:#1f2937',
        'box-shadow:0 2px 8px rgba(0,0,0,0.18)',
        'white-space:nowrap',
        'display:none'
      ].join(';')
      document.body.appendChild(el)
      this.fenceTooltip = el
    }
    return this.fenceTooltip
  }

  showFenceTooltip(fence, e) {
    const tip = this.getFenceTooltip()
    tip.textContent = fence.name || 'Cerca'
    tip.style.display = 'block'
    if (e.domEvent) this._positionTooltip(e.domEvent)
  }

  moveFenceTooltip(e) {
    if (e.domEvent) this._positionTooltip(e.domEvent)
  }

  hideFenceTooltip() {
    if (this.fenceTooltip) this.fenceTooltip.style.display = 'none'
  }

  _positionTooltip(domEvent) {
    const tip = this.fenceTooltip
    if (!tip) return
    const offset = 14
    tip.style.left = (domEvent.clientX + offset) + 'px'
    tip.style.top  = (domEvent.clientY + offset) + 'px'
  }

}